import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Trash2, Plus, Minus, Package, ShoppingCart } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { trackPixelPurchase } from '../utils/analytics';
import SharedPageLayout from './SharedPageLayout';

const CartPage = ({ navigateHome, navigateToProducts, navigateToCart, navigateToProfile, cart, setCart, clearCart }) => {
  const navigate = useNavigate();
  const { isAuthenticated, getIdToken } = useAuth();
  const { formatPrice, convertPrice, currency } = useCurrency();
  const [loadingCheckout, setLoadingCheckout] = useState(false);
  const [showAddressForm, setShowAddressForm] = useState(false);
  
  // Address form state
  const [billingAddress, setBillingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '', // Will be state for USA or province for Canada
    zipCode: '', // Will be ZIP for USA or postal code for Canada
    country: 'United States'
  });
  
  const [shippingAddress, setShippingAddress] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '', // Will be state for USA or province for Canada
    zipCode: '', // Will be ZIP for USA or postal code for Canada
    country: 'United States'
  });
  
  const [sameAsShipping, setSameAsShipping] = useState(true);
  const [addressErrors, setAddressErrors] = useState({});

  // Discount code (applied at checkout — server re-validates)
  const [discountCodeInput, setDiscountCodeInput] = useState('');
  const [appliedDiscount, setAppliedDiscount] = useState(null); // { code, discount_pct, description } or null
  const [discountStatus, setDiscountStatus] = useState({ type: 'idle', message: '' });
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const validateAndApplyDiscount = async () => {
    const code = (discountCodeInput || '').trim();
    if (!code) {
      setDiscountStatus({ type: 'error', message: 'Enter a code first.' });
      return;
    }
    setValidatingDiscount(true);
    setDiscountStatus({ type: 'idle', message: '' });
    try {
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      // Send cart context so server can check min_spend, exclude_from_*, and per-user limits.
      const cartSubtotal = getCartTotal();
      const headers = { 'Content-Type': 'application/json' };
      // Include auth token if user is logged in (for per-user limit enforcement)
      try {
        const idToken = isAuthenticated && (await getIdToken());
        if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
      } catch (_) {
        // ignore — anonymous validation still works (per-user limits unenforced)
      }
      const response = await fetch(`${apiUrl}/api/discount-codes/validate`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          code,
          cart_subtotal: cartSubtotal,
          cart_items: cart,
        }),
      });
      const data = await response.json();
      if (data && data.valid) {
        setAppliedDiscount({
          code: data.code,
          discount_pct: data.discount_pct,
          description: data.description || '',
          applicable_subtotal: data.applicable_subtotal,
          exclude_from_print_orders: !!data.exclude_from_print_orders,
          exclude_from_shop_orders: !!data.exclude_from_shop_orders,
        });
        setDiscountStatus({ type: 'success', message: `${data.discount_pct}% off applied.` });
      } else {
        setAppliedDiscount(null);
        setDiscountStatus({ type: 'error', message: (data && data.reason) || 'Invalid code.' });
      }
    } catch (e) {
      console.error('Discount validation failed', e);
      setAppliedDiscount(null);
      setDiscountStatus({ type: 'error', message: 'Could not validate code, please try again.' });
    } finally {
      setValidatingDiscount(false);
    }
  };

  const removeDiscount = () => {
    setAppliedDiscount(null);
    setDiscountCodeInput('');
    setDiscountStatus({ type: 'idle', message: '' });
  };

  // Address validation
  const validateAddress = (address, type) => {
    const errors = {};
    const isUSA = address.country === 'United States';
    
    if (!address.firstName.trim()) errors[`${type}FirstName`] = 'First name is required';
    if (!address.lastName.trim()) errors[`${type}LastName`] = 'Last name is required';
    if (!address.email.trim()) errors[`${type}Email`] = 'Email is required';
    if (!address.phone.trim()) errors[`${type}Phone`] = 'Phone number is required';
    if (!address.address1.trim()) errors[`${type}Address1`] = 'Address is required';
    if (!address.city.trim()) errors[`${type}City`] = 'City is required';
    if (!address.state.trim()) {
      errors[`${type}State`] = isUSA ? 'State is required' : 'Province is required';
    }
    if (!address.zipCode.trim()) {
      errors[`${type}ZipCode`] = isUSA ? 'ZIP code is required' : 'Postal code is required';
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (address.email && !emailRegex.test(address.email)) {
      errors[`${type}Email`] = 'Please enter a valid email';
    }
    
    // Postal code validation for Canada (format: A1A 1A1)
    if (!isUSA && address.zipCode) {
      const canadaPostalRegex = /^[A-Za-z]\d[A-Za-z] \d[A-Za-z]\d$/;
      if (!canadaPostalRegex.test(address.zipCode)) {
        errors[`${type}ZipCode`] = 'Please enter a valid postal code (A1A 1A1)';
      }
    }
    
    // ZIP code validation for USA (5 digits or 5+4)
    if (isUSA && address.zipCode) {
      const usaZipRegex = /^\d{5}(-\d{4})?$/;
      if (!usaZipRegex.test(address.zipCode)) {
        errors[`${type}ZipCode`] = 'Please enter a valid ZIP code (12345 or 12345-6789)';
      }
    }
    
    return errors;
  };

  // Handle same as shipping checkbox
  const handleSameAsShipping = (checked) => {
    setSameAsShipping(checked);
    if (checked) {
      setBillingAddress({ ...shippingAddress });
    } else {
      // Reset billing address but keep the same country as shipping
      setBillingAddress({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address1: '',
        address2: '',
        city: '',
        state: '',
        zipCode: '',
        country: shippingAddress.country
      });
    }
  };

  // Calculate tax based on shipping address
  const calculateTax = (subtotal) => {
    const country = shippingAddress.country;
    const province = shippingAddress.state;
    
    // No tax for US orders
    if (country === 'United States') {
      return { taxAmount: 0, taxLabel: 'Tax (US)', taxRate: 0 };
    }
    
    // Canadian tax rates
    if (country === 'Canada') {
      // HST provinces
      const hstProvinces = {
        'Ontario': 0.13,
        'ON': 0.13,
        'New Brunswick': 0.15,
        'NB': 0.15,
        'Newfoundland and Labrador': 0.15,
        'NL': 0.15,
        'Nova Scotia': 0.14,
        'NS': 0.14,
        'Prince Edward Island': 0.15,
        'PEI': 0.15,
        'PE': 0.15
      };
      
      // GST only provinces (5%)
      const gstOnlyProvinces = ['Alberta', 'AB', 'Manitoba', 'MB', 'Saskatchewan', 'SK', 
                                'Northwest Territories', 'NT', 'Nunavut', 'NU', 'Yukon', 'YT'];
      
      // Check for HST provinces
      if (hstProvinces[province]) {
        const rate = hstProvinces[province];
        return { 
          taxAmount: subtotal * rate, 
          taxLabel: `HST (${(rate * 100).toFixed(0)}%)`, 
          taxRate: rate 
        };
      }
      
      // British Columbia: GST (5%) + PST (7%) = 12%
      if (province === 'British Columbia' || province === 'BC') {
        const gst = subtotal * 0.05;
        const pst = subtotal * 0.07;
        return { 
          taxAmount: gst + pst, 
          taxLabel: 'GST (5%) + PST (7%)', 
          taxRate: 0.12,
          breakdown: { gst, pst }
        };
      }
      
      // Quebec: GST (5%) + QST (9.975%) = 14.975%
      if (province === 'Quebec' || province === 'QC') {
        const gst = subtotal * 0.05;
        const qst = subtotal * 0.09975;
        return { 
          taxAmount: gst + qst, 
          taxLabel: 'GST (5%) + QST (9.975%)', 
          taxRate: 0.14975,
          breakdown: { gst, qst }
        };
      }
      
      // GST only provinces (5%)
      if (gstOnlyProvinces.includes(province)) {
        return { 
          taxAmount: subtotal * 0.05, 
          taxLabel: 'GST (5%)', 
          taxRate: 0.05 
        };
      }
      
      // Default for Canada if province not selected - show GST only
      return { 
        taxAmount: subtotal * 0.05, 
        taxLabel: 'GST (5%)', 
        taxRate: 0.05 
      };
    }
    
    // Default - no tax
    return { taxAmount: 0, taxLabel: 'Tax', taxRate: 0 };
  };

  // Get current tax info
  const getTaxInfo = () => {
    return calculateTax(getCartTotal());
  };

  const updateQuantity = (productId, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(productId);
      return;
    }

    setCart(cart.map(item => 
      item.id === productId 
        ? { ...item, quantity: newQuantity }
        : item
    ));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.id !== productId));
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * (item.quantity || 1)), 0);
  };
  
  // Get shop items total only (excludes print quotes and marketplace prints)
  const getShopItemsTotal = () => {
    return cart
      .filter(item => item.type !== 'print_quote' && item.type !== 'marketplace_print' && item.type !== 'marketplace_design')
      .reduce((total, item) => total + (item.price * (item.quantity || 1)), 0);
  };
  
  // Check if cart contains only digital items (no shipping required)
  const isDigitalOnly = () => {
    return cart.length > 0 && cart.every(item => item.type === 'marketplace_design');
  };
  
  // Check if cart contains large/oversize print orders
  // Large print threshold: volume > 500 cm³ (approximately 8x8x8 cm)
  const LARGE_PRINT_VOLUME_THRESHOLD = 500;
  
  const hasLargePrintOrder = () => {
    return cart.some(item => {
      // Check instant quote print orders
      if (item.type === 'print_quote' && item.quote_data?.volume) {
        return item.quote_data.volume > LARGE_PRINT_VOLUME_THRESHOLD;
      }
      // Check marketplace print orders - check both top-level and print_config volume
      if (item.type === 'marketplace_print') {
        const volume = item.volume || item.print_config?.volume || 0;
        return volume > LARGE_PRINT_VOLUME_THRESHOLD;
      }
      return false;
    });
  };
  
  // Check if cart has any print orders (for shipping display)
  const hasPrintOrders = () => {
    return cart.some(item => item.type === 'print_quote' || item.type === 'marketplace_print');
  };
  
  // Get the actual shipping cost from print quotes and marketplace prints
  // These already have shipping calculated by the backend
  const getPrintOrdersShipping = () => {
    let totalPrintShipping = 0;
    cart.forEach(item => {
      // For instant quote print orders - shipping is in quote_data.price_breakdown.shipping_cost
      if (item.type === 'print_quote') {
        const shippingCost = item.quote_data?.price_breakdown?.shipping_cost || 0;
        console.log('Print quote shipping:', shippingCost, item);
        totalPrintShipping += shippingCost;
      }
      // For marketplace print orders - shipping is in price_breakdown.shipping_cost
      if (item.type === 'marketplace_print') {
        const shippingCost = item.price_breakdown?.shipping_cost || 0;
        console.log('Marketplace print shipping:', shippingCost, item);
        totalPrintShipping += shippingCost;
      }
    });
    return totalPrintShipping;
  };
  
  // Check if cart has print orders that need shipping
  const hasPrintOrdersWithShipping = () => {
    return cart.some(item => 
      (item.type === 'print_quote' && item.quote_data?.price_breakdown?.shipping_cost > 0) ||
      (item.type === 'marketplace_print' && item.price_breakdown?.shipping_cost > 0)
    );
  };
  
  // Check if eligible for free shipping on shop products ONLY
  // Free shipping applies to:
  // - Shop orders over $100 (physical products only, not print orders)
  // Does NOT apply to:
  // - Any print orders (they ALWAYS have their own calculated shipping)
  // - Digital-only orders (no shipping needed at all)
  const isShopEligibleForFreeShipping = () => {
    const shopTotal = getShopItemsTotal();
    return shopTotal >= 100;
  };

  // Get total shipping cost
  // Print orders ALWAYS use their backend-calculated shipping (never free)
  // Shop products use flat rate or free shipping based on threshold
  const getShippingCost = () => {
    if (isDigitalOnly()) return 0;
    
    let totalShipping = 0;
    
    // Add shipping from print orders (already calculated by backend - NEVER free)
    totalShipping += getPrintOrdersShipping();
    
    // Add shipping for shop products (flat rate or free based on threshold)
    const shopTotal = getShopItemsTotal();
    if (shopTotal > 0 && !isShopEligibleForFreeShipping()) {
      totalShipping += 9.99;
    }
    
    return totalShipping;
  };
  
  // Determine what to display for shipping
  const getShippingDisplay = () => {
    if (isDigitalOnly()) {
      return null; // Don't show shipping for digital only
    }
    
    const printShipping = getPrintOrdersShipping();
    const shopTotal = getShopItemsTotal();
    const totalShipping = getShippingCost();
    
    // If we have print orders, always show the calculated shipping (never "FREE")
    if (hasPrintOrders()) {
      return formatPrice(totalShipping);
    }
    
    // For shop products only - show FREE if eligible
    if (shopTotal > 0 && isShopEligibleForFreeShipping()) {
      return 'FREE';
    }
    
    // Default flat rate for shop products
    if (shopTotal > 0) {
      return formatPrice(9.99);
    }
    
    return formatPrice(totalShipping);
  };
  
  // Check if any part of the order has free shipping
  const hasAnyFreeShipping = () => {
    const shopTotal = getShopItemsTotal();
    return shopTotal >= 100 && shopTotal > 0 && !hasPrintOrders();
  };

  const getCartItemCount = () => {
    return cart.reduce((total, item) => total + item.quantity, 0);
  };

  const handleCheckout = async () => {
    if (!isAuthenticated) {
      alert('Please log in to proceed with checkout');
      return;
    }

    // Show address form first
    if (!showAddressForm) {
      setShowAddressForm(true);
      return;
    }

    // Validate addresses
    const shippingErrors = validateAddress(shippingAddress, 'shipping');
    const billingErrors = sameAsShipping ? {} : validateAddress(billingAddress, 'billing');
    const allErrors = { ...shippingErrors, ...billingErrors };

    if (Object.keys(allErrors).length > 0) {
      setAddressErrors(allErrors);
      alert('Please fill in all required address fields');
      return;
    }

    setLoadingCheckout(true);

    try {
      // Get Firebase ID token
      const idToken = await getIdToken();
      if (!idToken) {
        alert('Authentication error. Please log in again.');
        return;
      }

      // Create cart checkout with Stripe
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      let apiUrl = backendUrl;
      if (apiUrl.includes('None') || !apiUrl || apiUrl === '') {
        apiUrl = '';
      }

      // For now, we'll create individual orders for each cart item
      // In a full implementation, you'd want a dedicated cart checkout endpoint
      const currentUrl = window.location.origin;
      const successUrl = `${currentUrl}/?payment=success&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${currentUrl}/?payment=cancelled`;

      // Calculate total amount including shipping and tax
      // Base prices are in CAD - always convert to USD for Stripe
      // This triggers Stripe's multi-currency option allowing customers to choose CAD or USD
      const conversionRate = 0.73; // CAD to USD
      const subtotalCAD = getCartTotal();
      const shippingCAD = getShippingCost();
      const taxInfo = calculateTax(subtotalCAD);
      const taxCAD = taxInfo.taxAmount;
      const totalCAD = subtotalCAD + shippingCAD + taxCAD;
      
      // Convert to USD for Stripe (Stripe will offer CAD/USD choice to customer)
      const subtotal = subtotalCAD * conversionRate;
      const shipping = shippingCAD * conversionRate;
      const tax = taxCAD * conversionRate;
      let totalAmount = totalCAD * conversionRate;
      // NOTE: server-side will re-validate the code and re-apply the discount
      // to the final total — we still adjust the displayed/sent total here so
      // Stripe shows the right charge amount.
      if (appliedDiscount && appliedDiscount.discount_pct > 0) {
        totalAmount = totalAmount * (1 - appliedDiscount.discount_pct / 100);
      }

      // Prepare checkout data with addresses
      // Always send USD - Stripe will offer customer the choice of CAD or USD at checkout
      const checkoutData = {
        cart_items: cart.map(item => ({
          id: item.id,
          listing_id: item.listing_id,
          product_id: item.id,
          name: item.name,
          manufacturer: item.manufacturer,
          category: item.category,
          quantity: item.quantity || 1,
          price: item.price * conversionRate,  // Convert to USD
          type: item.type,
          license_type: item.license_type,
          print_config: item.print_config,
          price_breakdown: item.price_breakdown,
          quote_data: item.quote_data
        })),
        subtotal: subtotal,
        shipping: shipping,
        tax: tax,
        total_amount: totalAmount,
        currency: 'usd',  // Always USD - Stripe offers CAD/USD choice to customer
        success_url: successUrl,
        cancel_url: cancelUrl,
        shipping_address: shippingAddress,
        billing_address: sameAsShipping ? shippingAddress : billingAddress,
        discount_code: (appliedDiscount && appliedDiscount.code) || ''
      };

      const response = await fetch(`${apiUrl}/api/cart/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        credentials: 'include',
        body: JSON.stringify(checkoutData)
      });

      if (response.ok) {
        const result = await response.json();
        
        // Redirect to Stripe checkout
        if (result.checkout_url) {
          // Fire Meta Pixel Purchase right before redirect. Stripe-hosted checkout
          // means we don't get a guaranteed return-page render, so this is the
          // best client-side moment to mark conversion. Server webhooks should
          // handle authoritative attribution.
          try {
            const totalCAD = (cart || []).reduce((sum, it) => sum + (Number(it.price) || 0) * (Number(it.quantity) || 1), 0);
            trackPixelPurchase({
              value: totalCAD,
              currency: 'CAD',
              content_ids: (cart || []).map(it => it.id || it.quote_id).filter(Boolean),
            });
          } catch (e) { /* tracking is best-effort */ }
          window.location.href = result.checkout_url;
        } else {
          throw new Error('No checkout URL received');
        }
      } else {
        const errorData = await response.json();
        // Log technical detail to console for debugging — show user a friendly message only.
        console.error('Checkout failed:', errorData);
        alert('Sorry, we couldn\'t start checkout. Please try again, or contact us at hello@create3dworks.com if the issue persists.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Sorry, we couldn\'t start checkout. Please try again, or contact us at hello@create3dworks.com if the issue persists.');
    } finally {
      setLoadingCheckout(false);
    }
  };

  return (
    <SharedPageLayout 
      navigateHome={navigateHome}
      navigateToCart={navigateToCart}
      navigateToProfile={navigateToProfile}
      cartCount={cart.length}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cart.length === 0 ? (
          /* Empty Cart */
          <div className="text-center py-12">
            <ShoppingCart className="w-16 h-16 text-slate-400 mx-auto mb-4" />
            <h2 className="text-2xl font-semibold text-slate-600 mb-4">Your cart is empty</h2>
            <p className="text-slate-500 mb-6">Add some products to your cart to get started</p>
            <Button onClick={navigateToProducts} className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900">
              Start Shopping
            </Button>
          </div>
        ) : (
          /* Cart Contents */
          <div className="space-y-6">
            {/* Cart Header */}
            <div className="text-center">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Shopping Cart</h1>
              <p className="text-slate-600">{getCartItemCount()} items in your cart</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-6">
                {/* Cart Items List */}
                <div className="space-y-4">
                  {cart.map(item => (
                  <Card key={item.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-start space-x-4">
                        {/* Product Image */}
                        <img
                          src={item.image_url || item.thumbnail || '/placeholder-product.png'}
                          alt={item.name}
                          className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
                          onError={(e) => { e.target.src = '/placeholder-product.png'; }}
                        />
                        
                        {/* Product Details */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between mb-2">
                            <div>
                              <h3 className="font-medium text-slate-800 truncate">{item.name}</h3>
                              
                              {/* Show description for print quotes, otherwise show manufacturer/category */}
                              {item.type === 'print_quote' && item.description ? (
                                <div className="text-sm text-slate-600 mt-1 space-y-0.5 whitespace-pre-line">
                                  {item.description}
                                </div>
                              ) : item.type === 'marketplace_print' ? (
                                <div className="text-sm text-slate-600 mt-1 space-y-0.5">
                                  <p>Print Order • {item.license_type} License</p>
                                  {item.license_fee > 0 && (
                                    <p className="text-xs text-slate-500">
                                      (Includes {formatPrice(item.license_fee)} license fee)
                                    </p>
                                  )}
                                </div>
                              ) : item.type === 'marketplace_design' ? (
                                <div className="text-sm text-slate-600 mt-1">
                                  <p>Digital Download • {item.license_type} License</p>
                                </div>
                              ) : (
                                <>
                                  <p className="text-sm text-slate-600">{item.manufacturer}</p>
                                  <p className="text-sm text-slate-500">{item.category}</p>
                                </>
                              )}
                            </div>
                            <button
                              onClick={() => removeFromCart(item.id)}
                              className="text-slate-400 hover:text-red-500 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                          
                          {/* Quantity and Price */}
                          <div className="flex items-center justify-between">
                            {item.type === 'print_quote' ? (
                              // Print quotes have qty + settings locked into the quote.
                              // Editing must go back through the wizard so price/discounts
                              // are recomputed correctly. Show qty as a read-only badge.
                              <div className="flex items-center gap-3">
                                <div
                                  className="px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg text-sm font-semibold text-[#0007b5]"
                                  data-testid="cart-print-qty-badge"
                                >
                                  QTY: {item.quantity}
                                </div>
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => navigate(`/get-started?service=on-demand-3d-print&edit_cart_id=${encodeURIComponent(item.id)}`)}
                                  className="border-[#0007b5] text-[#0007b5] hover:bg-[#0007b5]/5"
                                  data-testid={`cart-edit-quote-${item.id}`}
                                >
                                  Edit Quote
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                >
                                  <Minus className="w-3 h-3" />
                                </button>
                                <Input
                                  type="number"
                                  value={item.quantity}
                                  onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1)}
                                  className="w-16 text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                  min="1"
                                />
                                <button
                                  onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                  className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition-colors"
                                >
                                  <Plus className="w-3 h-3" />
                                </button>
                              </div>
                            )}

                            <div className="text-right">
                              <div className="font-semibold text-lg">{formatPrice(item.price * item.quantity)}</div>
                              <div className="text-sm text-slate-500">{formatPrice(item.price)} each</div>
                            </div>
                          </div>

                          {/* Print-quote specific: advise customers not to fiddle with qty here */}
                          {item.type === 'print_quote' && (
                            <p
                              className="mt-3 text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                              data-testid="cart-print-qty-note"
                            >
                              <strong className="text-slate-700">Quantity locked in cart.</strong>{' '}
                              For accurate unit pricing, please use <span className="font-semibold text-[#0007b5]">Edit Quote</span> to adjust the quantity or any settings. Changing it here would not recalculate your discounts.
                            </p>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>

                {/* Address Forms - Show when checkout is initiated */}
                {showAddressForm && (
                  <div className="space-y-6">
                    {/* Shipping Address */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Shipping Address</CardTitle>
                        <CardDescription>Where should we deliver your order?</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* Country Selection */}
                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                          <select
                            value={shippingAddress.country}
                            onChange={(e) => setShippingAddress({...shippingAddress, country: e.target.value, state: '', zipCode: ''})}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          >
                            <option value="United States">United States</option>
                            <option value="Canada">Canada</option>
                          </select>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                            <Input
                              value={shippingAddress.firstName}
                              onChange={(e) => setShippingAddress({...shippingAddress, firstName: e.target.value})}
                              className={addressErrors.shippingFirstName ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingFirstName && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingFirstName}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                            <Input
                              value={shippingAddress.lastName}
                              onChange={(e) => setShippingAddress({...shippingAddress, lastName: e.target.value})}
                              className={addressErrors.shippingLastName ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingLastName && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingLastName}</p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                            <Input
                              type="email"
                              value={shippingAddress.email}
                              onChange={(e) => setShippingAddress({...shippingAddress, email: e.target.value})}
                              className={addressErrors.shippingEmail ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingEmail && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingEmail}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                            <Input
                              type="tel"
                              value={shippingAddress.phone}
                              onChange={(e) => setShippingAddress({...shippingAddress, phone: e.target.value})}
                              className={addressErrors.shippingPhone ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingPhone && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingPhone}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                          <Input
                            value={shippingAddress.address1}
                            onChange={(e) => setShippingAddress({...shippingAddress, address1: e.target.value})}
                            className={addressErrors.shippingAddress1 ? 'border-red-500' : ''}
                          />
                          {addressErrors.shippingAddress1 && (
                            <p className="text-red-500 text-xs mt-1">{addressErrors.shippingAddress1}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                          <Input
                            value={shippingAddress.address2}
                            onChange={(e) => setShippingAddress({...shippingAddress, address2: e.target.value})}
                          />
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                            <Input
                              value={shippingAddress.city}
                              onChange={(e) => setShippingAddress({...shippingAddress, city: e.target.value})}
                              className={addressErrors.shippingCity ? 'border-red-500' : ''}
                            />
                            {addressErrors.shippingCity && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingCity}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {shippingAddress.country === 'United States' ? 'State *' : 'Province *'}
                            </label>
                            <Input
                              value={shippingAddress.state}
                              onChange={(e) => setShippingAddress({...shippingAddress, state: e.target.value})}
                              className={addressErrors.shippingState ? 'border-red-500' : ''}
                              placeholder={shippingAddress.country === 'United States' ? 'e.g., CA' : 'e.g., ON'}
                            />
                            {addressErrors.shippingState && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingState}</p>
                            )}
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-slate-700 mb-1">
                              {shippingAddress.country === 'United States' ? 'ZIP Code *' : 'Postal Code *'}
                            </label>
                            <Input
                              value={shippingAddress.zipCode}
                              onChange={(e) => setShippingAddress({...shippingAddress, zipCode: e.target.value})}
                              className={addressErrors.shippingZipCode ? 'border-red-500' : ''}
                              placeholder={shippingAddress.country === 'United States' ? '12345' : 'A1A 1A1'}
                            />
                            {addressErrors.shippingZipCode && (
                              <p className="text-red-500 text-xs mt-1">{addressErrors.shippingZipCode}</p>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>

                    {/* Billing Address */}
                    <Card>
                      <CardHeader>
                        <CardTitle>Billing Address</CardTitle>
                        <CardDescription>Address for payment processing</CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="sameAsShipping"
                            checked={sameAsShipping}
                            onChange={(e) => handleSameAsShipping(e.target.checked)}
                            className="rounded border-gray-300"
                          />
                          <label htmlFor="sameAsShipping" className="text-sm font-medium text-slate-700">
                            Same as shipping address
                          </label>
                        </div>

                        {!sameAsShipping && (
                          <div className="space-y-4">
                            {/* Country Selection */}
                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Country *</label>
                              <select
                                value={billingAddress.country}
                                onChange={(e) => setBillingAddress({...billingAddress, country: e.target.value, state: '', zipCode: ''})}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                              >
                                <option value="United States">United States</option>
                                <option value="Canada">Canada</option>
                              </select>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">First Name *</label>
                                <Input
                                  value={billingAddress.firstName}
                                  onChange={(e) => setBillingAddress({...billingAddress, firstName: e.target.value})}
                                  className={addressErrors.billingFirstName ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingFirstName && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingFirstName}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Last Name *</label>
                                <Input
                                  value={billingAddress.lastName}
                                  onChange={(e) => setBillingAddress({...billingAddress, lastName: e.target.value})}
                                  className={addressErrors.billingLastName ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingLastName && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingLastName}</p>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                                <Input
                                  type="email"
                                  value={billingAddress.email}
                                  onChange={(e) => setBillingAddress({...billingAddress, email: e.target.value})}
                                  className={addressErrors.billingEmail ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingEmail && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingEmail}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">Phone *</label>
                                <Input
                                  type="tel"
                                  value={billingAddress.phone}
                                  onChange={(e) => setBillingAddress({...billingAddress, phone: e.target.value})}
                                  className={addressErrors.billingPhone ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingPhone && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingPhone}</p>
                                )}
                              </div>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 1 *</label>
                              <Input
                                value={billingAddress.address1}
                                onChange={(e) => setBillingAddress({...billingAddress, address1: e.target.value})}
                                className={addressErrors.billingAddress1 ? 'border-red-500' : ''}
                              />
                              {addressErrors.billingAddress1 && (
                                <p className="text-red-500 text-xs mt-1">{addressErrors.billingAddress1}</p>
                              )}
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-slate-700 mb-1">Address Line 2</label>
                              <Input
                                value={billingAddress.address2}
                                onChange={(e) => setBillingAddress({...billingAddress, address2: e.target.value})}
                              />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">City *</label>
                                <Input
                                  value={billingAddress.city}
                                  onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                                  className={addressErrors.billingCity ? 'border-red-500' : ''}
                                />
                                {addressErrors.billingCity && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingCity}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                  {billingAddress.country === 'United States' ? 'State *' : 'Province *'}
                                </label>
                                <Input
                                  value={billingAddress.state}
                                  onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                                  className={addressErrors.billingState ? 'border-red-500' : ''}
                                  placeholder={billingAddress.country === 'United States' ? 'e.g., CA' : 'e.g., ON'}
                                />
                                {addressErrors.billingState && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingState}</p>
                                )}
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-slate-700 mb-1">
                                  {billingAddress.country === 'United States' ? 'ZIP Code *' : 'Postal Code *'}
                                </label>
                                <Input
                                  value={billingAddress.zipCode}
                                  onChange={(e) => setBillingAddress({...billingAddress, zipCode: e.target.value})}
                                  className={addressErrors.billingZipCode ? 'border-red-500' : ''}
                                  placeholder={billingAddress.country === 'United States' ? '12345' : 'A1A 1A1'}
                                />
                                {addressErrors.billingZipCode && (
                                  <p className="text-red-500 text-xs mt-1">{addressErrors.billingZipCode}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Address Form Actions */}
                    <div className="flex justify-between">
                      <Button 
                        onClick={() => setShowAddressForm(false)}
                        variant="outline"
                      >
                        Back to Cart
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              {/* Cart Summary */}
              <div className="lg:col-span-1">
                <Card className="sticky top-24">
                  <CardHeader>
                    <CardTitle>Order Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Subtotal ({getCartItemCount()} items):</span>
                        <span>{formatPrice(getCartTotal())}</span>
                      </div>
                      {!isDigitalOnly() && (
                        <div className="flex justify-between text-sm">
                          <span>Shipping:</span>
                          <span>{getShippingDisplay()}</span>
                        </div>
                      )}
                      {getTaxInfo().taxAmount > 0 ? (
                        <div className="flex justify-between text-sm">
                          <span>{getTaxInfo().taxLabel}:</span>
                          <span>{formatPrice(getTaxInfo().taxAmount)}</span>
                        </div>
                      ) : shippingAddress.country === 'United States' ? (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>Tax:</span>
                          <span>$0.00 (No sales tax)</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-sm text-slate-500">
                          <span>Tax:</span>
                          <span>Select province for tax</span>
                        </div>
                      )}
                    </div>
                    
                    <Separator />
                    
                    {/* Discount code */}
                    <div data-testid="cart-discount-code-section">
                      {appliedDiscount ? (
                        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg px-3 py-2">
                          <div className="text-sm">
                            <span className="font-semibold text-green-700">{appliedDiscount.code}</span>
                            <span className="text-green-600 ml-2">−{appliedDiscount.discount_pct}%</span>
                          </div>
                          <button
                            type="button"
                            onClick={removeDiscount}
                            className="text-xs text-green-700 hover:text-green-900 underline"
                            data-testid="cart-remove-discount"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Label htmlFor="cart-discount-code" className="text-sm">Discount code</Label>
                          <div className="flex gap-2">
                            <Input
                              id="cart-discount-code"
                              value={discountCodeInput}
                              onChange={(e) => setDiscountCodeInput(e.target.value.toUpperCase())}
                              placeholder="Enter code"
                              className="flex-1"
                              data-testid="cart-discount-code-input"
                              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); validateAndApplyDiscount(); } }}
                            />
                            <Button
                              type="button"
                              variant="outline"
                              onClick={validateAndApplyDiscount}
                              disabled={validatingDiscount || !discountCodeInput.trim()}
                              data-testid="cart-apply-discount-btn"
                            >
                              {validatingDiscount ? 'Checking…' : 'Apply'}
                            </Button>
                          </div>
                          {discountStatus.message && (
                            <p className={`text-xs ${discountStatus.type === 'error' ? 'text-red-600' : discountStatus.type === 'success' ? 'text-green-600' : 'text-slate-500'}`}>
                              {discountStatus.message}
                            </p>
                          )}
                        </div>
                      )}
                    </div>

                    <Separator />
                    
                    <div className="flex justify-between font-semibold text-lg">
                      <span>Total:</span>
                      <div>
                        {appliedDiscount ? (
                          <div className="text-right">
                            <div className="text-sm text-slate-400 line-through font-normal">
                              {formatPrice(getCartTotal() + getShippingCost() + getTaxInfo().taxAmount)}
                            </div>
                            <div data-testid="cart-total-with-discount">
                              {formatPrice((getCartTotal() + getShippingCost() + getTaxInfo().taxAmount) * (1 - appliedDiscount.discount_pct / 100))}
                            </div>
                          </div>
                        ) : (
                          <div data-testid="cart-total">{formatPrice(getCartTotal() + getShippingCost() + getTaxInfo().taxAmount)}</div>
                        )}
                      </div>
                    </div>

                    <Button 
                      onClick={handleCheckout}
                      disabled={loadingCheckout}
                      className="w-full bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-700 hover:to-blue-900 py-3"
                    >
                      {loadingCheckout ? 'Processing...' : `Checkout - ${formatPrice((appliedDiscount ? (getCartTotal() + getShippingCost() + getTaxInfo().taxAmount) * (1 - appliedDiscount.discount_pct / 100) : (getCartTotal() + getShippingCost() + getTaxInfo().taxAmount)))}`}
                    </Button>

                    {!isAuthenticated && (
                      <p className="text-center text-sm text-slate-500">
                        🔒 Sign in required to proceed with checkout
                      </p>
                    )}

                    <div className="text-center">
                      <Button onClick={navigateToProducts} variant="outline" className="w-full">
                        Continue Shopping
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        )}
      </div>
    </SharedPageLayout>
  );
};

export default CartPage;